import { applyTheme, renderThemeOptions } from './core/theme.js';
import { initPwa, watchForUpdates, checkForUpdates, applyUpdate, fetchVersionInfo } from './core/pwa.js';
import { dismissMenuHint, maybeShowMenuHint, toggleMenu, closeMenu } from './core/menu.js';
import { dismissCpuHint, maybeShowCpuHint } from './core/cpu-hint.js';
import {
    showPlainModal, hidePlainModal, isModalOpen
} from './core/modal.js';
import {
    initDB, generateGameId,
    savePlayersToStorage, loadPlayersFromStorage,
    saveGameToHistory, getAllGames
} from './core/db.js';
import { DIFFICULTY_TIERS, DEFAULT_DIFFICULTY, findDifficultyTier } from './core/ai-difficulty.js';
import { safeGet, safeSet, safeRemove } from './core/local-storage.js';
import * as sync from './core/sync.js';
import { track, isTrackingDisabled } from './core/metrics.js';
import * as feedbackApi from './core/feedback.js';
import * as feedbackUpdates from './core/feedback-updates.js';
import { mountFeedbackBoard } from './components/feedback-board.js';
import qrcode from './vendor/qrcode-generator/qrcode.mjs';
import Sortable from './vendor/sortablejs/sortable.esm.js';
import halveItGame from './games/halveit/controller.js';
import cricketGame from './games/cricket/controller.js';
import tacticsGame from './games/tactics/controller.js';
import randomCricketGame from './games/random-cricket/controller.js';
import cutthroatGame from './games/cutthroat/controller.js';
import x01Game from './games/501/controller.js';
import killerGame from './games/killer/controller.js';
import cricketStyleKillerGame from './games/cricket-style-killer/controller.js';
import snookerGame from './games/snooker/controller.js';
import { HALVEIT_ICON, CRICKET_ICON, X01_ICON, KILLER_ICON, SNOOKER_ICON } from './games/icons.js';

const APP_NAME = 'Darted';
// More descriptive than bare APP_NAME for the landing/document <title> and
// its matching og:title - a browser tab or search result showing just
// "Darted" gives no hint what the app actually is; every other screen's
// title already says "Darted - <Game Name>", so this keeps that pattern
// instead of being the one generic exception.
const LANDING_TITLE = `${APP_NAME} - Darts Scoreboard for Friends`;

// Keeps <link rel="canonical"> pointed at the clean path for the current
// route (no query string - e.g. /501?debug=true canonicalizes to /501), so
// crawlers consolidate ranking signals onto one URL per page instead of
// treating query variants as separate duplicate pages.
function updateCanonicalUrl(path) {
    const url = `${location.origin}${path}`;
    document.getElementById('canonicalLink').href = url;
    // The JSON-LD block describes the app as a whole (see index.html), not
    // this specific page, but its url field should still point at wherever
    // the app was actually reached from rather than a hardcoded "/".
    const structuredData = JSON.parse(document.getElementById('structuredData').textContent);
    structuredData.url = url;
    document.getElementById('structuredData').textContent = JSON.stringify(structuredData);
}

// Keeps <meta name="description"> and the Open Graph tags in sync with the
// current route, reusing each game's existing rules-summary description
// (definition.description, already shown on the setup screen's info
// button) rather than maintaining separate SEO copy per game.
function updateMetaTags(title, description, path) {
    document.getElementById('metaDescription').setAttribute('content', description);
    document.getElementById('ogTitle').setAttribute('content', title);
    document.getElementById('ogDescription').setAttribute('content', description);
    document.getElementById('ogUrl').setAttribute('content', `${location.origin}${path}`);
    // Same image for every route (there's no per-game artwork) - OG/Twitter
    // still require an absolute URL for it, so it's set here rather than
    // left as the static relative default in index.html's <head>.
    const imageUrl = `${location.origin}/images/og-image.png`;
    document.getElementById('ogImage').setAttribute('content', imageUrl);
    document.getElementById('twitterTitle').setAttribute('content', title);
    document.getElementById('twitterDescription').setAttribute('content', description);
    document.getElementById('twitterImage').setAttribute('content', imageUrl);
}

// The landing screen groups games into families - a family with one game
// (Halve It today) is a single clickable card; a family with several
// variations (Cricket now has "Tactics", "Random Cricket" and "Cutthroat"
// alongside it) shows its name/icon as a header with a vertical button per
// variation instead. Add a new game to an existing family's `games` list, or
// a new family entry entirely, and it shows up on the landing screen and in
// Statistics with zero other changes.
const FAMILIES = [
    {
        id: 'x01', name: '501', icon: X01_ICON, games: [x01Game],
        description: 'The traditional darts game. Count down to exactly zero before anyone else.'
    },
    {
        id: 'halveit', name: 'Halve It', icon: HALVEIT_ICON, games: [halveItGame],
        description: 'Score across 13 rounds - miss and your total gets halved.'
    },
    {
        id: 'killer', name: 'Killer', icon: KILLER_ICON, games: [killerGame, cricketStyleKillerGame],
        description: 'Claim a number, become a Killer, and take out everyone else\'s lives.'
    },
    {
        id: 'cricket', name: 'Cricket', icon: CRICKET_ICON, games: [cricketGame, tacticsGame, randomCricketGame, cutthroatGame],
        description: 'Close your numbers and score before your opponents do.'
    },
    {
        id: 'snooker', name: 'Snooker', icon: SNOOKER_ICON, games: [snookerGame],
        description: 'Pot reds and colors in order, snooker-style, on the dartboard.'
    }
];
const GAMES = FAMILIES.flatMap(family => family.games);

window.applyTheme = applyTheme;
initPwa();
window.toggleMenu = () => {
    toggleMenu();
    // Fresh unread count by the time the user reaches "Feedback & Bugs".
    if (document.getElementById('sideMenu').classList.contains('active')) feedbackUpdates.refreshUnread();
};
window.closeMenu = closeMenu;

let players = [];
// Per-player extra config keyed by name, for players that need more than
// just a name - today that's only Computer players ({ type: 'cpu',
// difficulty }); a human player has no entry here at all. Kept as a
// separate map (rather than turning `players` into an array of objects) so
// every game controller, which already keys everything off the bare name
// string, needs no changes to keep working. See core/ai-difficulty.js for
// the difficulty tiers themselves.
let playerConfigs = {};
// State for the "Add Players" row's own controls, reset back to these
// defaults on every addPlayer() (not per-game - a Computer opponent's
// default difficulty has nothing to do with which game is selected).
let addPlayerType = 'human';
let addPlayerDifficulty = DEFAULT_DIFFICULTY;
let scores = {};
// Optional per-player extra text a game can supply alongside its scores,
// shown to the left of the score in the victory modal's standings (e.g.
// x01's per-player average) - {} for games that don't provide any.
let standingLabels = {};
// Optional override for the game type name shown in history/stats and the
// victory modal (e.g. x01 reports the actual starting score, "301", instead
// of its fixed definition name "501") - null falls back to the definition's
// own name, same as before this existed.
let gameTypeLabel = null;
let currentGameId = null;
let activeGame = null; // the GAMES entry currently being set up/played, or null on the landing screen
let fullscreen = false;
// The pending "show the victory modal" timer from the most recent completion
// - re-armed on every game update (see handleGameChange) so an undo shortly
// after a real completion can cancel it, and cleared on a fresh game start
// so a stale one from an abandoned game can never fire against a new one.
let finishGameTimer = null;
// The pending "re-enable scrolling" timer for the victory modal's entrance
// animations (see finishGame()) - same re-arm-on-every-call/clear-on-new-
// game shape as finishGameTimer above, for the same reason.
let victoryAnimationTimer = null;
// The non-controller-state bits of the currently-active game's envelope
// (see core/sync.js) - set once at startGame()/on joining a remote game,
// reused by every later handleGameChange push so it doesn't need to be
// re-read from the setup screen's DOM on every single score update. Kept
// whether or not a session is open, so a game started solo can still be
// published if the client starts hosting partway through it.
let sessionEnvelopeMeta = null;
// True only while applying an envelope that arrived over the wire - guards
// the sync push hooks below from re-broadcasting a change that didn't
// originate locally (an infinite echo between clients otherwise).
let applyingRemoteEnvelope = false;

function findGame(id) {
    return GAMES.find(game => game.definition.id === id);
}

// Toggled at exactly the points a game truly starts/stops being "in
// progress" (see call sites) - covers both warning before an accidental tab
// close and keeping the screen awake while actively playing.
function setGameInProgress(enabled) {
    if (enabled) {
        window.addEventListener('beforeunload', handleBeforeUnload);
        acquireWakeLock();
    } else {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        releaseWakeLock();
    }
}

// --- Screen Wake Lock - keeps the screen from dimming/locking while a game
// is being actively played, released again the moment it isn't (see
// setGameInProgress's call sites). The OS releases the lock on its own
// whenever the tab is backgrounded (switching apps, locking the phone), so
// it has to be reacquired on visibilitychange too, or it'd silently stay
// off for the rest of the game after returning to the tab. ---

let wakeLock = null;
let wantsWakeLock = false; // whether a game is currently in progress, independent of whether the lock is actually held right now

async function acquireWakeLock() {
    wantsWakeLock = true;
    if (!('wakeLock' in navigator)) return;
    try {
        wakeLock = await navigator.wakeLock.request('screen');
    } catch (e) {
        wakeLock = null; // e.g. tab hidden at request time - harmless, just no lock until it's re-tried
    }
}

function releaseWakeLock() {
    wantsWakeLock = false;
    if (wakeLock) {
        wakeLock.release();
        wakeLock = null;
    }
}

document.addEventListener('visibilitychange', () => {
    if (wantsWakeLock && document.visibilityState === 'visible' && !wakeLock) {
        acquireWakeLock();
    }
});

function handleBeforeUnload(e) {
    const hasProgress = activeGame && activeGame.hasProgress && activeGame.hasProgress();
    if (document.getElementById('game').style.display !== 'none' && hasProgress) {
        const message = 'You have a game in progress. Are you sure you want to leave?';
        e.preventDefault();
        e.returnValue = message;
        return message;
    }
}

// --- Screen routing (landing / setup / game), driven off real paths
// (/501, /cricket, ...) via the History API - each game gets a real,
// crawlable/bookmarkable/back-button-friendly URL. index.html has a
// <base href="/"> so every relative resource reference still resolves
// correctly regardless of which path the page was actually loaded from, and
// server/index.js serves the same app shell for any unrecognized path (a
// direct hit/refresh/crawl on /cricket is a real request to the server,
// unlike the old #-fragment scheme where nothing after # was ever sent). ---

function hideAllScreens() {
    // Actually leaving the game screen (back button, Home, Restart - all of
    // them route through here via showSetup()/showLanding()) has to stop a
    // Computer player's turn right along with it, or it just keeps
    // simulating and committing turns in the background - its own overlay
    // was appended straight to document.body, not anywhere this hides, so
    // it would otherwise keep animating on top of whatever's shown next.
    // Not every game controller has this (only x01 plays a CPU turn at
    // all), hence the guard. Re-mounting the *same* game while it's still
    // showing (a fresh startGame(), an observer-mode remount) never reaches
    // here, since isGameScreenActive() only flips true->false on a genuine
    // screen change.
    if (isGameScreenActive() && activeGame && typeof activeGame.stopCpuTurn === 'function') {
        activeGame.stopCpuTurn();
    }
    document.getElementById('landing').style.display = 'none';
    document.getElementById('setup').style.display = 'none';
    document.getElementById('game').style.display = 'none';
}

function renderGameList() {
    const list = document.getElementById('gameList');
    list.innerHTML = FAMILIES.map(family => {
        const singleGame = family.games.length === 1 ? family.games[0] : null;
        return `
            <div class="game-card" data-family="${family.id}"${singleGame ? ' role="button" tabindex="0"' : ''}>
                ${singleGame ? '<span class="game-card-start-icon"><i class="fa-solid fa-play"></i></span>' : ''}
                <div class="game-card-header">
                    <span class="game-card-icon">${family.icon}</span>
                    <span class="game-card-title">${family.name}</span>
                </div>
                <p class="game-card-description">${family.description}</p>
                ${singleGame ? '' : `
                    <div class="game-card-variations">
                        ${family.games.map(game => `
                            <button class="game-variation-btn" data-game="${game.definition.id}">
                                <span>${game.definition.name}</span>
                                <i class="fa-solid fa-play"></i>
                            </button>
                        `).join('')}
                    </div>
                `}
            </div>
        `;
    }).join('');

    // A single-variation family's whole card navigates straight to its one
    // game; a multi-variation family's card is just a frame - only its
    // per-variation buttons navigate.
    list.querySelectorAll('.game-card[role="button"]').forEach(card => {
        const family = FAMILIES.find(f => f.id === card.dataset.family);
        const goToGame = () => { navigateTo(`/${family.games[0].definition.id}`); };
        card.addEventListener('click', goToGame);
        card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToGame(); }
        });
    });
    list.querySelectorAll('.game-variation-btn').forEach(btn => {
        btn.addEventListener('click', () => { navigateTo(`/${btn.dataset.game}`); });
    });
}

function showLanding() {
    hideAllScreens();
    activeGame = null;
    document.getElementById('landing').style.display = 'block';
    document.getElementById('navbarTitleText').textContent = APP_NAME;
    document.getElementById('navbarTitle').classList.add('on-landing');
    document.title = LANDING_TITLE;
    updateCanonicalUrl('/');
    updateMetaTags(LANDING_TITLE, 'A scoreboard for playing darts with friends - 501, Cricket, Killer, Halve It and more. Play solo on one device, or together across several, synced live.', '/');
    document.getElementById('homeBtn').style.display = 'none';
    document.getElementById('restartBtn').style.display = 'none';
    document.getElementById('switchGameMenuItem').style.display = 'none';
    setGameInProgress(false);
    renderGameList();
    updateSessionUI();
}

function updateStartButton() {
    document.getElementById('startBtn').disabled = !activeGame || playersForActiveGame().length < activeGame.definition.minPlayers;
}

// --- Generic per-game setup-screen config rows: Match Format's Starting
// score/Sets/Legs on x01, Lives per player/Ring required on Killer,
// Doubles-triples/Turn mode on Snooker, and any future game's own choices -
// one shared renderer + storage scheme instead of a bespoke block per game.
// Each row (definition.js's own configRows) is rendered in x01's original
// shape: a label, a toggle-button group, a separator line under the row,
// and - only if at least one of its options declares one - a dimmed
// description of whichever option is currently selected.
//
// A row's own id doubles as its storage key, in one flat object covering
// every game - so two games that happen to declare a row with the same id
// (both Killer variants' "killer-lives") automatically share that one
// remembered value, the same way they always have, with no extra grouping
// concept needed. ---

const GAME_CONFIG_STORAGE_KEY = 'halveren-game-config';

function loadGameConfigFromStorage() {
    const raw = safeGet(GAME_CONFIG_STORAGE_KEY);
    if (!raw) return {};
    try {
        return JSON.parse(raw) || {};
    } catch (e) {
        return {};
    }
}

function saveConfigRowValueToStorage(rowId, value) {
    const stored = loadGameConfigFromStorage();
    stored[rowId] = value;
    safeSet(GAME_CONFIG_STORAGE_KEY, JSON.stringify(stored));
}

// Sets one row's active button (both the visual .active state and the
// toggle-group's own dataset.value, same convention the old per-game toggle
// groups used) and, if it has any, updates its description to match
// whichever option is now selected.
function setConfigRowValue(row, value) {
    const group = document.getElementById(`${row.id}Toggle`);
    group.dataset.value = value;
    group.querySelectorAll('.toggle-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.value === value);
    });
    const description = document.getElementById(`${row.id}Description`);
    if (description) {
        const option = row.options.find(opt => opt.value === value);
        description.textContent = (option && option.description) || '';
    }
}

// Rebuilds #gameConfigRows from scratch for whichever game is now active -
// a different game means a different (or empty) set of rows, so there's
// nothing worth patching incrementally. Restores each row to its last
// stored value (falling back to the row's own default), and wires every
// button's click to update + persist that row.
function renderConfigRows(game) {
    const container = document.getElementById('gameConfigRows');
    const rows = game.definition.configRows || [];
    if (rows.length === 0) {
        container.innerHTML = '';
        return;
    }

    container.innerHTML = '<h3 class="section-title">Options</h3>' + rows.map(row => {
        const accentClass = row.accent ? ` toggle-group-${row.accent}` : '';
        const buttonsHTML = row.options.map(opt =>
            `<button type="button" class="toggle-btn" data-value="${opt.value}">${opt.label}</button>`
        ).join('');
        const hasDescriptions = row.options.some(opt => opt.description);
        return `
            <div class="config-row">
                <label>${row.label}</label>
                <div class="toggle-group${accentClass}" id="${row.id}Toggle" data-value="">${buttonsHTML}</div>
                ${hasDescriptions ? `<p class="option-description" id="${row.id}Description"></p>` : ''}
            </div>
        `;
    }).join('');

    const stored = loadGameConfigFromStorage();
    rows.forEach(row => {
        setConfigRowValue(row, String(stored[row.id] ?? row.default));
        document.getElementById(`${row.id}Toggle`).querySelectorAll('.toggle-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                setConfigRowValue(row, btn.dataset.value);
                saveConfigRowValueToStorage(row.id, btn.dataset.value);
            });
        });
    });
}

// Reads back whatever's currently selected in every row the active game
// declares, as { [rowId]: value } - a row marked `numeric` comes back as a
// Number (lives, darts totals, ...), everything else as the plain string
// value its options use (a ring, a color, a turn mode, ...).
function getConfigRowValues(game) {
    const values = {};
    (game.definition.configRows || []).forEach(row => {
        const raw = document.getElementById(`${row.id}Toggle`).dataset.value;
        values[row.id] = row.numeric ? Number(raw) : raw;
    });
    return values;
}

function showSetup(game) {
    hideAllScreens();
    activeGame = game;
    document.getElementById('setup').style.display = 'block';
    document.getElementById('setupTitle').textContent = game.definition.name;
    document.getElementById('navbarTitleText').textContent = game.definition.name;
    document.getElementById('navbarTitle').classList.remove('on-landing');
    document.title = `${APP_NAME} - ${game.definition.name}`;
    updateCanonicalUrl(`/${game.definition.id}`);
    updateMetaTags(`${APP_NAME} - ${game.definition.name}`, game.definition.description, `/${game.definition.id}`);
    document.getElementById('homeBtn').style.display = 'flex';
    document.getElementById('restartBtn').style.display = 'none';
    document.getElementById('switchGameMenuItem').style.display = 'flex';
    setGameInProgress(false);

    // The Human/Computer toggle (and everything it reveals) only makes
    // sense for a game whose controller actually knows what to do with a
    // Computer player - see games/shared/x01-game.js's own
    // supportsCpuPlayers comment. Forcing back to Human (rather than just
    // hiding the toggle) also resets addPlayerType itself, so switching
    // games mid-session can't leave it silently stuck on 'cpu' with no
    // visible control to change it back.
    const cpuSupported = gameSupportsCpuPlayers(game);
    document.getElementById('playerTypeToggle').style.display = cpuSupported ? '' : 'none';
    if (!cpuSupported) {
        setAddPlayerType('human');
        dismissCpuHint();
    } else {
        maybeShowCpuHint();
    }

    // A different game can mean a different filtered view of the same
    // shared roster (see playersForActiveGame()) - re-rendered here since
    // switching games via the setup screen doesn't reload the page.
    updatePlayersList();
    updateStartButton();

    renderConfigRows(game);
}

function showGame() {
    hideAllScreens();
    document.getElementById('game').style.display = 'block';
    document.getElementById('restartBtn').style.display = 'block';
    // Normally redundant with showSetup() (always called first when a
    // player starts their own game from that game's own setup screen), but
    // a client auto-following a host into an already-started game via
    // sync.onEnvelope goes straight to showGame() - without this, its title
    // (and the Switch Game menu item, hidden on the landing screen) would
    // just be left at whatever it was before (e.g. APP_NAME from the
    // landing screen) instead of the actual game being played.
    document.getElementById('switchGameMenuItem').style.display = 'flex';
    document.getElementById('navbarTitleText').textContent = activeGame.definition.name;
    document.getElementById('navbarTitle').classList.remove('on-landing');
    document.title = `${APP_NAME} - ${activeGame.definition.name}`;
    updateCanonicalUrl(`/${activeGame.definition.id}`);
    updateMetaTags(`${APP_NAME} - ${activeGame.definition.name}`, activeGame.definition.description, `/${activeGame.definition.id}`);
}

function handleRoute() {
    const gameId = location.pathname.replace(/^\/+/, '');
    if (!gameId) { showLanding(); return; }
    const game = findGame(gameId);
    if (!game) { showLanding(); return; }
    showSetup(game);
}

// Pushes a real, indexable per-game URL (e.g. /501, /cricket) and renders it
// immediately - pushState() never fires any event on its own (unlike the
// old location.hash assignment, which the browser turned into a
// `hashchange` automatically), so every caller that changes the URL this
// way pairs it with an explicit handleRoute() call. The browser's own
// back/forward buttons are the one path that doesn't go through this - see
// the popstate listener below, which is the only other place a route
// actually gets rendered.
function navigateTo(path) {
    // Preserves the query string (?debug=1, etc.) - the old location.hash
    // assignment this replaced never touched .search at all, so a bare path
    // here would be a regression: it replaces the whole URL, silently
    // dropping any query string a plain path change never used to.
    history.pushState({ route: true }, '', path + location.search);
    handleRoute();
}

function isGameScreenActive() {
    return document.getElementById('game').style.display !== 'none';
}

// The browser back button doesn't go through navigateTo() (it's a history
// navigation, not a call we make ourselves), so there's no natural place to
// intercept it - setup and the game screen also share one path, so
// back-from-game and back-from-setup are indistinguishable by URL alone.
// Pushing a same-path "guard" entry when a game starts means the first
// back-press just pops that entry (no visible URL change) and fires
// `popstate` instead, which we *can* intercept: if there's progress, ask
// before actually leaving, and re-arm the guard either way so a later
// back-press is caught again.
function pushHistoryGuard() {
    // pathname + search, not just pathname - unlike the old location.hash
    // (a fragment-only reference, which URL resolution rules always resolve
    // against the rest of the current URL unchanged), a bare path here
    // would replace the whole URL and silently drop any query string
    // (?debug=1, etc.) the instant a game starts.
    history.pushState({ guard: true }, '', location.pathname + location.search);
}

// Set by a programmatic history.back() we fire ourselves (see
// discardGamePopstateGuard below) so the resulting popstate is swallowed
// here instead of being treated as a real back-press.
let suppressNextGamePopstate = false;

// Every back/forward navigation lands here, covering two very different
// cases: on the game screen, a guard entry (see pushHistoryGuard above)
// makes a back-press land here instead of ever actually changing the URL,
// handled below exactly as before. Everywhere else, this *is* a real URL
// change (landing <-> a game's setup screen) - unlike the old hash scheme,
// nothing else fires on a plain back/forward press, so handleRoute() has to
// be called explicitly here too, not just from navigateTo().
window.addEventListener('popstate', () => {
    if (suppressNextGamePopstate) { suppressNextGamePopstate = false; return; }
    if (!isGameScreenActive()) { handleRoute(); return; }

    const hasProgress = activeGame && activeGame.hasProgress && activeGame.hasProgress();
    if (!hasProgress) {
        // Nothing to lose - drop straight back to this game's own setup
        // screen, since the guard entry already absorbed this back-press
        // with no visible change on its own (same path as the game screen).
        doGoToSetup();
        return;
    }

    pushHistoryGuard();
    openDiscardConfirm({
        title: 'Leave Game?',
        body: 'Are you sure you want to leave? All current scores and progress will be lost.',
        confirmLabel: 'Leave',
        action: doGoToSetup,
        // Confirming will navigate to the setup screen at this exact same
        // path, not away from it - so unlike the "leave via the Home
        // button" flow, the guard entry just pushed above would otherwise
        // linger as a dead, same-path entry that silently swallows the
        // next real back-press with no visible effect. Only clean it up
        // when confirmed - Cancel (or dismissing this dialog with back)
        // must leave it in place so a later back-press is caught again.
        cleanupGuardOnConfirm: true
    });
});

// Removes the just-re-armed guard entry (see the popstate listener above)
// once we know it's actually being left behind for good, via a real but
// suppressed history.back() - the only way to drop an entry once pushed.
function discardGamePopstateGuard() {
    suppressNextGamePopstate = true;
    return new Promise((resolve) => {
        window.addEventListener('popstate', function onPop() {
            window.removeEventListener('popstate', onPop);
            resolve();
        }, { once: true });
        history.back();
    });
}

// Back-button-from-game lands on this same game's setup/config screen, not
// all the way at the landing page - the path is already `/<gameId>` (it
// never changed when the game started), so there's no URL to restore, just
// the setup screen itself.
function doGoToSetup() {
    showSetup(activeGame);
    // Unlike doGoHome()/doRestart(), this doesn't discard anything - the
    // mounted controller stays alive underneath, hidden - so it's purely a
    // local view change for this client and every other controlling one
    // (nobody else's screen should jump because of it). An observer has
    // nothing to watch while whoever's playing is off configuring
    // something instead, though, so it's sent a lightweight signal to go
    // back to its own landing "waiting" card - see the 'config' branch in
    // the sync.onEnvelope handler below, which only observers act on.
    if (!applyingRemoteEnvelope && sync.isInSession() && !sync.isObserver()) {
        sync.pushEnvelope({ phase: 'config' });
    }
}

function goHome() {
    if (isGameScreenActive() && activeGame && activeGame.hasProgress && activeGame.hasProgress()) {
        openDiscardConfirm({
            title: 'Leave Game?',
            body: 'Are you sure you want to leave? All current scores and progress will be lost.',
            confirmLabel: 'Leave',
            action: doGoHome
        });
        return;
    }
    doGoHome();
}

function doGoHome() {
    // showLanding() first, same order the old hash-based version needed -
    // no longer strictly required (pushState never fires popstate the way
    // location.hash assignment sometimes did in practice), but there's no
    // reason to disturb a correct order. Deliberately a plain pushState, not
    // navigateTo() - showLanding() just ran, so there's nothing left for a
    // second handleRoute() call to do. Query string preserved for the same
    // reason navigateTo() preserves it - see its own comment.
    showLanding();
    history.pushState({ route: true }, '', '/' + location.search);
    sessionEnvelopeMeta = null;
    if (!applyingRemoteEnvelope && sync.isInSession() && !sync.isObserver()) {
        sync.pushEnvelope({ phase: 'idle' });
    }
}

// --- Player setup (shared across every game - the roster persists in
// IndexedDB independent of which game is currently selected). ---

// Whether the given game's own controller knows what to do with a Computer
// player at all (see games/shared/x01-game.js's own supportsCpuPlayers
// comment) - `game` can be null (no game selected yet, e.g. before the
// first showSetup()), which is treated as "yes" purely so nothing filters
// or hides itself before there's an actual game context to filter for.
function gameSupportsCpuPlayers(game) {
    return !game || !!game.definition.supportsCpuPlayers;
}

// The roster actually usable for the currently active game - the full
// shared `players` list (see the comment above), minus any Computer
// players when that game doesn't support them at all. A CPU player
// configured while a *different*, CPU-capable game was selected shouldn't
// silently show up in this one's player list, count toward its minPlayers,
// or get handed to a controller that has no idea what to do with one -
// see updatePlayersList(), updateStartButton() and startGame().
function playersForActiveGame() {
    if (gameSupportsCpuPlayers(activeGame)) return players;
    return players.filter(player => !(playerConfigs[player] && playerConfigs[player].type === 'cpu'));
}

// The next unused "CPU N" name - offered as a starting point the moment
// someone switches the type toggle to Computer, purely so adding several
// bots in a row never collides on name; still a normal, editable text field.
function nextCpuName() {
    let n = 1;
    while (players.includes(`CPU ${n}`)) n++;
    return `CPU ${n}`;
}

// Add Player only ever does anything with a non-empty, not-already-taken
// name (see addPlayer() below) - disabled otherwise, rather than silently
// no-opping on click. Re-run on every keystroke (wired below) and whenever
// the roster or the input's own value changes some other way (adding,
// removing, switching Human/Computer, loading a saved roster).
function updateAddPlayerButtonState() {
    const name = document.getElementById('playerName').value.trim();
    document.getElementById('addPlayerBtn').disabled = !name || players.includes(name);
}

// The Human/Computer toggle above the name field - switching to Computer
// reveals the difficulty dropdown and suggests the next free "CPU N" name;
// switching back to Human hides it again and clears that suggestion if it
// was never edited.
function setAddPlayerType(type) {
    addPlayerType = type;
    if (type === 'cpu') dismissCpuHint();
    document.getElementById('playerTypeToggle').querySelectorAll('.toggle-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.value === type);
    });
    const input = document.getElementById('playerName');
    document.getElementById('difficultyField').style.display = type === 'cpu' ? '' : 'none';
    if (type === 'cpu') {
        if (!input.value.trim()) input.value = nextCpuName();
    } else if (/^CPU \d+$/.test(input.value.trim())) {
        input.value = '';
    }
    updateAddPlayerButtonState();
}

// --- Difficulty dropdown (the Computer-only control next to the name field).
// A custom listbox rather than a native <select> so each option can show its
// own plain-language trait, not just a name - see core/ai-difficulty.js for
// the tier list itself. ---

// A tier's throw spread, as fixed (not randomised - this is decorative, and
// re-rendering the menu shouldn't visibly reshuffle it) dart positions
// relative to the target's centre, tightening from Beginner to Expert. Each
// pair is a [x, y] offset in units of the target's own radius (so ±1 reaches
// the outer ring), same layout the setup mockup used.
const TIER_SCATTER = [
    [[0.9, -0.3], [-0.6, 0.8], [0.3, 0.95], [-0.85, -0.4], [0.1, -0.9]],
    [[0.6, -0.2], [-0.4, 0.55], [0.2, 0.6], [-0.55, -0.3], [0.05, -0.6]],
    [[0.4, -0.15], [-0.3, 0.35], [0.15, 0.4], [-0.35, -0.2], [0.05, -0.4]],
    [[0.22, -0.1], [-0.15, 0.2], [0.1, 0.22], [-0.2, -0.12], [0.02, -0.22]],
    [[0.08, -0.04], [-0.06, 0.08], [0.04, 0.09], [-0.08, -0.05], [0.01, -0.08]]
];

// Renders a small dartboard with that tier's 5 throws plotted on it - one
// glance shows what "Advanced" actually means at the oche, rather than
// leaving it to the label and description alone. Colored with that tier's
// own --tier-N token (see styles.css's per-theme tier ramp), same one
// .tier-badge-* uses on a Computer player's row card, so the dropdown
// option and the card it produces read as the same tier at a glance.
function tierTargetSVG(tierIndex, size) {
    size = size || 40;
    const c = size / 2;
    const r = size / 2 - 2;
    const tierColor = `var(--tier-${tierIndex + 1})`;
    const rings = [1, 0.72, 0.46, 0.22, 0.08];
    const ringFill = ['var(--surface)', 'var(--surface-2)', 'var(--surface)', 'var(--surface-2)', tierColor];
    let svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true">`;
    rings.forEach((ratio, i) => {
        svg += `<circle cx="${c}" cy="${c}" r="${(r * ratio).toFixed(1)}" fill="${ringFill[i]}" stroke="var(--border)" stroke-width="1"/>`;
    });
    TIER_SCATTER[tierIndex].forEach(([dx, dy]) => {
        const px = (c + dx * r).toFixed(1);
        const py = (c + dy * r).toFixed(1);
        svg += `<circle cx="${px}" cy="${py}" r="2.4" fill="${tierColor}" stroke="var(--surface)" stroke-width="1"/>`;
    });
    svg += `</svg>`;
    return svg;
}

function renderDifficultyMenu() {
    const menu = document.getElementById('difficultyMenu');
    menu.innerHTML = DIFFICULTY_TIERS.map((t, i) => `
        <div class="difficulty-option${t.id === addPlayerDifficulty ? ' selected' : ''}" role="option" aria-selected="${t.id === addPlayerDifficulty}" data-id="${t.id}">
            <span class="difficulty-option-target">${tierTargetSVG(i)}</span>
            <span class="difficulty-option-text">
                <span class="difficulty-option-name">${t.label}</span>
                <span class="difficulty-option-desc">${t.description}</span>
            </span>
        </div>
    `).join('');
    menu.querySelectorAll('.difficulty-option').forEach(opt => {
        opt.addEventListener('click', () => selectDifficulty(opt.dataset.id));
    });
    document.getElementById('difficultySelectLabel').textContent = findDifficultyTier(addPlayerDifficulty).label;
}

function selectDifficulty(id) {
    addPlayerDifficulty = id;
    closeDifficultyMenu();
    renderDifficultyMenu();
}

// The menu is wider than the trigger it hangs off (see positionDifficultyMenu
// below) - a 200px-wide trigger next to the name field would otherwise force
// every option's description to wrap across 3+ cramped lines. Repositioned
// on open and on resize/orientation change while it's open, since the card's
// own width (which the menu is sized to) can change under it.
function repositionOpenDifficultyMenu() {
    if (document.getElementById('difficultyMenu').classList.contains('open')) {
        positionDifficultyMenu();
    }
}

// Sizes and positions #difficultyMenu to span the setup card's full content
// width (between its left/right padding) rather than just the trigger
// button's own width - .difficulty-field (the menu's positioned ancestor) is
// usually much narrower, sharing the row with the name field. `left`/`right`
// on an absolutely-positioned element are offsets from their positioned
// ancestor's padding edges, so translating the card's own padding edges into
// offsets from the field's left/right edges is enough to make the menu span
// the card instead - no change to the positioning ancestor itself needed.
function positionDifficultyMenu() {
    const menu = document.getElementById('difficultyMenu');
    const field = document.getElementById('difficultyField');
    const card = field.closest('.card');
    if (!card) return;

    const fieldRect = field.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const cardStyle = getComputedStyle(card);
    const cardPaddingLeft = parseFloat(cardStyle.paddingLeft) || 0;
    const cardPaddingRight = parseFloat(cardStyle.paddingRight) || 0;

    menu.style.left = `${(cardRect.left + cardPaddingLeft) - fieldRect.left}px`;
    menu.style.right = `${fieldRect.right - (cardRect.right - cardPaddingRight)}px`;
}

function toggleDifficultyMenu() {
    const menu = document.getElementById('difficultyMenu');
    const trigger = document.getElementById('difficultySelect');
    const opening = !menu.classList.contains('open');
    if (opening) positionDifficultyMenu();
    menu.classList.toggle('open', opening);
    trigger.setAttribute('aria-expanded', String(opening));
    if (opening) {
        // Deferred a tick so the click that opened the menu (which is still
        // bubbling up to document right now) doesn't immediately close it.
        setTimeout(() => document.addEventListener('click', closeDifficultyMenu, { once: true }), 0);
    }
}

function closeDifficultyMenu() {
    document.getElementById('difficultyMenu').classList.remove('open');
    document.getElementById('difficultySelect').setAttribute('aria-expanded', 'false');
}

window.addEventListener('resize', repositionOpenDifficultyMenu);

function addPlayer() {
    const input = document.getElementById('playerName');
    const name = input.value.trim();

    if (name && !players.includes(name)) {
        players.push(name);
        if (addPlayerType === 'cpu') {
            playerConfigs[name] = { type: 'cpu', difficulty: addPlayerDifficulty };
            input.value = nextCpuName();
        } else {
            input.value = '';
        }
        updatePlayersList();
        updateStartButton();
        updateAddPlayerButtonState();
        savePlayersToStorage(players, playerConfigs);
    }
}

// A small ⋮⋮ grip glyph, purely decorative (the whole row is the drag
// target, same as a plain chip always was - see initPlayersDragReorder
// below) but it's what tells the eye a full-width row is draggable, the way
// a compact pill chip's own edges used to.
const DRAG_HANDLE_SVG = '<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true"><circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/></svg>';

function updatePlayersList() {
    const list = document.getElementById('playersList');
    // The roster this game can actually use (see playersForActiveGame()'s
    // own comment) - a Computer player configured for a different, CPU-
    // capable game just doesn't appear here at all while a game that
    // doesn't support one is selected, rather than showing as a player
    // this game would then try (and not know how) to actually play.
    const visiblePlayers = playersForActiveGame();
    if (visiblePlayers.length === 0) {
        // Same spot the row cards would otherwise stack in, so the card
        // (whichever game is behind it) never opens on an empty gap between
        // "Players" and the config rows below it.
        list.innerHTML = '<p class="players-empty-hint">No players yet - add one above to get started.</p>';
        return;
    }
    list.innerHTML = visiblePlayers.map(player => {
        const config = playerConfigs[player];
        if (!config || config.type !== 'cpu') {
            return `
                <div class="player-card" data-player="${player}">
                    <span class="player-card-drag-handle">${DRAG_HANDLE_SVG}</span>
                    <span class="player-card-avatar">${player.trim().charAt(0).toUpperCase()}</span>
                    <span class="player-card-info">
                        <span class="player-card-name">${player}</span>
                        <span class="player-card-type-label">Human</span>
                    </span>
                    <span class="player-card-delete" data-player="${player}">×</span>
                </div>
            `;
        }
        const tier = findDifficultyTier(config.difficulty);
        const tierIndex = DIFFICULTY_TIERS.indexOf(tier);
        return `
            <div class="player-card player-card-cpu" data-player="${player}">
                <span class="player-card-drag-handle">${DRAG_HANDLE_SVG}</span>
                <span class="player-card-avatar player-card-avatar-cpu"><i class="fa-solid fa-robot"></i></span>
                <span class="player-card-info">
                    <span class="player-card-name-row">
                        <span class="player-card-name">${player}</span>
                        <span class="tier-badge tier-badge-${tier.id}">${tier.label}</span>
                    </span>
                    <span class="player-card-tier-trait">${tier.shortDescription}</span>
                </span>
                <span class="player-card-target">${tierTargetSVG(tierIndex, 32)}</span>
                <span class="player-card-delete" data-player="${player}">×</span>
            </div>
        `;
    }).join('');
    list.querySelectorAll('.player-card-delete').forEach(el => {
        el.addEventListener('click', () => removePlayer(el.dataset.player));
    });
}

function removePlayer(player) {
    players = players.filter(p => p !== player);
    delete playerConfigs[player];
    updatePlayersList();
    updateStartButton();
    updateAddPlayerButtonState(); // removing a name frees it up again, e.g. if it's still sitting in the input
    savePlayersToStorage(players, playerConfigs);
}

// --- Reordering players by dragging their row card vertically (turn order
// follows list order everywhere else) - SortableJS handles the actual
// pointer/touch tracking and the slide animation; this just reads the
// resulting DOM order back into `players` once a drag ends. ---

function initPlayersDragReorder() {
    Sortable.create(document.getElementById('playersList'), {
        animation: 150,
        handle: '.player-card-drag-handle', // only the grip glyph starts a drag - the rest of the row (name, ×) behaves normally
        filter: '.player-card-delete',
        preventOnFilter: false, // let the × still receive its own click instead of Sortable swallowing it
        onEnd: (evt) => {
            // The rendered list can be a filtered subset of `players` (see
            // playersForActiveGame()) - the DOM only ever contains that
            // subset, so reading it back directly (as if it were the whole
            // roster) would silently drop whichever players this game
            // filtered out of view. Each hidden player instead keeps its
            // own absolute slot, with the reordered visible names filling
            // the rest in their new order.
            const newVisibleOrder = [...evt.to.querySelectorAll('.player-card')].map(card => card.dataset.player);
            const newVisibleSet = new Set(newVisibleOrder);
            let nextVisible = 0;
            players = players.map(player => newVisibleSet.has(player) ? newVisibleOrder[nextVisible++] : player);
            savePlayersToStorage(players, playerConfigs);
        }
    });
}

// --- Game lifecycle ---

// { [player]: throwProfile } for whichever current players are configured
// as Computer players - built fresh from this device's own local
// playerConfigs (never from anything synced), so it's only ever non-empty
// on the one client that actually set them up on its own setup screen. A
// game controller that reads this (currently just x01 - see
// games/shared/x01-game-controller.js's mount()) uses that as the signal
// for which of its own turns to simulate and play automatically; every
// other connected client's own copy of this is empty, which is what keeps
// a CPU's turns from being independently re-simulated on more than one
// device in a shared session.
function buildCpuPlayers(roster = playersForActiveGame()) {
    const cpuPlayers = {};
    roster.forEach(player => {
        const config = playerConfigs[player];
        if (config && config.type === 'cpu') {
            cpuPlayers[player] = findDifficultyTier(config.difficulty).throwProfile;
        }
    });
    return cpuPlayers;
}

function startGame() {
    const gamePlayers = playersForActiveGame();
    if (!activeGame || gamePlayers.length < activeGame.definition.minPlayers) return;

    currentGameId = generateGameId();
    scores = {};
    standingLabels = {};
    gameTypeLabel = null;
    clearTimeout(finishGameTimer); // a stale timer from a just-abandoned game must never fire against this new one
    showGame();
    pushHistoryGuard();
    setGameInProgress(true);

    // getConfigRowValues() only ever returns keys for rows the active game
    // actually declares - so translating those generic { rowId: value }
    // pairs into the specific named properties each game's own mount()
    // already expects is just picking out whichever ones are there. Keeps
    // every controller's mount() signature exactly as it was before this
    // became a generic system.
    const rowValues = getConfigRowValues(activeGame);
    const matchConfig = (rowValues.bestOfSets !== undefined || rowValues.bestOfLegs !== undefined || rowValues.startingScore !== undefined)
        ? { bestOfSets: rowValues.bestOfSets, bestOfLegs: rowValues.bestOfLegs, startingScore: rowValues.startingScore }
        : undefined;
    const lives = rowValues['killer-lives'];
    const ring = rowValues.ring;
    const includeBull = rowValues['killer-include-bull'];
    const multiplier = rowValues.multiplier;
    const turnMode = rowValues.turnMode;

    // How many of this game's players (if any) are Computer-controlled, and
    // which difficulty each one is - only ever non-empty on the device that
    // actually set them up (see buildCpuPlayers()'s own comment), so a
    // shared session's other participants never double-count the same CPU
    // players. Carried on game_started (rather than its own event) so
    // "how often are CPU opponents used" is just a query against that one
    // event's props, alongside everything else about the same game.
    const cpuDifficulties = gamePlayers
        .map(player => playerConfigs[player])
        .filter(config => config && config.type === 'cpu')
        .map(config => config.difficulty);

    // Only ever reached from this device's own "Start Game" tap - a client
    // joining an in-progress session mounts the game straight off the synced
    // envelope instead (see the sync.onEnvelope handler below), so this
    // can't double-count a game once per participant.
    track('game_started', {
        gameId: currentGameId,
        gameType: activeGame.definition.id,
        playerCount: gamePlayers.length,
        multiplayer: sync.isInSession(),
        cpuPlayerCount: cpuDifficulties.length,
        cpuDifficulties,
        config: rowValues
    });

    activeGame.mount(document.getElementById('scoreGrid'), {
        players: gamePlayers,
        gameId: currentGameId,
        onChange: handleGameChange,
        matchConfig,
        lives,
        ring,
        includeBull,
        multiplier,
        turnMode,
        cpuPlayers: buildCpuPlayers()
    });

    if (!applyingRemoteEnvelope) {
        sessionEnvelopeMeta = { gameType: activeGame.definition.id, players: gamePlayers, matchConfig, lives, ring, includeBull, multiplier, turnMode };
        pushSessionEnvelope();
    }
}

// The currently-active game's full envelope, or null when there isn't one.
function buildSessionEnvelope() {
    if (!sessionEnvelopeMeta || !activeGame) return null;
    return {
        phase: 'active',
        gameId: currentGameId,
        controllerState: activeGame.getState(),
        ...sessionEnvelopeMeta
    };
}

// Ships the currently-active game's full envelope to the room, if this
// client is hosting/playing in one - a no-op call is harmless (sync.js's own
// pushEnvelope() is a no-op when not in a session or when observing).
function pushSessionEnvelope() {
    const envelope = buildSessionEnvelope();
    if (envelope) sync.pushEnvelope(envelope);
}

function handleGameChange({ scores: newScores, complete, standingLabels: newStandingLabels, gameTypeLabel: newGameTypeLabel }) {
    scores = newScores;
    standingLabels = newStandingLabels || {};
    gameTypeLabel = newGameTypeLabel || null;
    // Clearing any pending timer on every update (not just when complete is
    // newly false) means an undo shortly after a real completion can never
    // let a stale, already-superseded "you won" fire later - only the most
    // recent completion state's own timer ever survives to run.
    clearTimeout(finishGameTimer);
    if (complete) {
        // Captured now, not read inside finishGame() itself - this same
        // handler runs identically whether `complete` arrived from this
        // device's own local action or from a remote envelope, but by the
        // time this deferred call actually fires, sync.onEnvelope's handler
        // has long since reset applyingRemoteEnvelope back to false either
        // way. Snapshotting it here is what lets finishGame() still tell
        // "my own action finished this match" apart from "I just heard
        // about someone else's finish" for metrics purposes.
        const isLocalFinish = !applyingRemoteEnvelope;
        finishGameTimer = setTimeout(() => finishGame(isLocalFinish), 500); // small delay for better UX
    } else if (isModalOpen('victoryModal')) {
        // Solo play can never reach this - the modal's own backdrop blocks
        // the board underneath, so Undo isn't clickable while it's open.
        // In a multiplayer session it very much can: another client's undo
        // of the winning move arrives here via setState()'s notifyChange()
        // while this client's victory modal is already open, and would
        // otherwise sit there showing a stale "you won" over a match that
        // no longer is one.
        closeVictoryModal();
    }
    if (!applyingRemoteEnvelope && sync.isInSession() && !sync.isObserver()) {
        pushSessionEnvelope();
    }
}

// Used for both the victory modal's standings and Statistics history rows -
// a real gold/silver hierarchy (1st primary, 2nd secondary) instead of just
// "the winner" vs. everyone else in plain text.
function rankColor(index) {
    if (index === 0) return 'var(--primary)';
    if (index === 1) return 'var(--secondary)';
    return 'var(--text)';
}

function finishGame(isLocalFinish = true) {
    saveGameToHistory({
        gameId: currentGameId,
        gameType: activeGame.definition.id,
        gameTypeLabel,
        players,
        scores,
        standingLabels
    });

    // Every connected client (host, every other controller, every observer)
    // independently reaches its own local "complete" render off the same
    // synced state - isLocalFinish (captured in handleGameChange, see there
    // for why it can't just be read from applyingRemoteEnvelope here) is
    // only true for whichever one device's own local action actually
    // produced this finish, so gating on it is what keeps a shared match
    // from counting once per participant instead of once. gameId lets a
    // later report line this up with this same match's game_started row
    // (and its playerCount/config) without needing to compute a duration
    // client-side.
    if (isLocalFinish) {
        track('game_finished', {
            gameId: currentGameId,
            gameType: activeGame.definition.id,
            playerCount: players.length,
            multiplayer: sync.isInSession()
        });
    }

    const sortedPlayers = activeGame.definition.rankPlayers(players, scores);
    const winner = sortedPlayers[0];

    document.getElementById('winnerName').textContent = winner;
    // x01's standings below already show every player's score (and avg) -
    // a single "winnerScore" headline would either repeat that or (for a
    // sets/legs score) need the same "1 - 0 - 0" listing redundantly, so it's
    // hidden there entirely rather than duplicated. Keyed off the presence
    // of a bestOfSets configRow (the same thing that gates the setup
    // screen's match-format controls) rather than the game's id, so another
    // game could opt into this without app.js special-casing it.
    const winnerScoreEl = document.getElementById('winnerScore');
    const hasMatchConfig = (activeGame.definition.configRows || []).some(row => row.id === 'bestOfSets');
    if (hasMatchConfig) {
        winnerScoreEl.style.display = 'none';
    } else {
        winnerScoreEl.style.display = '';
        winnerScoreEl.textContent = `${scores[winner]}`;
    }

    const standingsHTML = `
        <div class="standings-title">FINAL STANDINGS</div>
        ${sortedPlayers.map((player, index) => `
            <div class="standing-row">
                <span class="standing-row-name">${index + 1}. ${player}</span>
                <span class="standing-row-values">
                    ${standingLabels[player] ? `<span class="standing-row-label">${standingLabels[player]}</span>` : ''}
                    <span style="color: ${rankColor(index)}">${scores[player]}</span>
                </span>
            </div>
        `).join('')}
    `;
    document.getElementById('finalStandings').innerHTML = standingsHTML;

    // Only x01 controllers expose this (see matchStatsHTML in
    // games/shared/x01-game-controller.js) - every other game leaves the
    // section out entirely rather than showing an empty "Statistics" block.
    const statsEl = document.getElementById('victoryStats');
    if (typeof activeGame.matchStatsHTML === 'function') {
        statsEl.innerHTML = `<div class="standings-title">STATISTICS</div>${activeGame.matchStatsHTML()}`;
        statsEl.style.display = '';
    } else {
        statsEl.innerHTML = '';
        statsEl.style.display = 'none';
    }

    showPlainModal('victoryModal', 'victoryModalBackdrop');
    // The browser preserves scroll position across a display:none/block
    // toggle (showPlainModal's own show/hide mechanism), so without this a
    // long previous victory's scroll-down/across would otherwise still be
    // applied here, hiding the trophy/winner behind whatever was at that
    // offset. Has to happen after showPlainModal, not before - writing
    // scrollTop/scrollLeft while the element is still display:none has no
    // layout box to scroll, so the browser silently discards it and
    // restores the old offset once it becomes visible again.
    const victoryScrollBody = document.querySelector('#victoryModal .modal-scroll-body');
    victoryScrollBody.scrollTop = 0;
    victoryScrollBody.scrollLeft = 0;
    // Blocked while the trophy/winner/standings/stats entrance animations
    // (see .animated-grow/.animated-fade) are still visibly moving things
    // around - scrolling then looks broken. Not held for the full 2900ms
    // those animations actually take to finish (1400ms delay + 1500ms
    // duration on the last one, .standings/#victoryStats's animation-delay-3)
    // - by 1500ms everything's close enough to settled that unblocking
    // early reads as responsive rather than sluggish. The scroll indicator
    // stays hidden for the same stretch, cleared by clearTimeout below if
    // another victory (somehow) lands before this one's timer fires.
    victoryScrollBody.classList.add('victory-animating');
    clearTimeout(victoryAnimationTimer);
    victoryAnimationTimer = setTimeout(() => {
        victoryScrollBody.classList.remove('victory-animating');
        updateVictoryScrollIndicator();
    }, 1500);
    setGameInProgress(false);
}

// Shown only while there's more content below the fold (scrollHeight beyond
// clientHeight) AND the user hasn't scrolled yet - it disappears the moment
// either stops being true, rather than sitting there as a permanent
// decoration once someone's already found the rest of the content.
function updateVictoryScrollIndicator() {
    const scrollBody = document.querySelector('#victoryModal .modal-scroll-body');
    const scrollable = scrollBody.scrollHeight > scrollBody.clientHeight + 1;
    document.getElementById('victoryScrollIndicator').style.display = (scrollable && scrollBody.scrollTop <= 0) ? 'flex' : 'none';
}
document.querySelector('#victoryModal .modal-scroll-body').addEventListener('scroll', updateVictoryScrollIndicator);

// Scrolls straight to the statistics section (x01 only - see finishGame())
// rather than just nudging the scroll position, since that's the one thing
// actually hidden below the fold worth jumping to; on a game with no
// statistics section, falls back to scrolling all the way down instead of
// doing nothing.
document.getElementById('victoryScrollIndicator').addEventListener('click', () => {
    const scrollBody = document.querySelector('#victoryModal .modal-scroll-body');
    const statsEl = document.getElementById('victoryStats');
    // The "STATISTICS" title itself (statsEl's first child), not statsEl's
    // own box - statsEl carries a top margin for spacing after the
    // standings above it, and landing on that margin instead of the title
    // would leave a visible gap under the header rather than the title
    // sitting flush against it.
    const target = statsEl.style.display !== 'none' ? statsEl.querySelector('.standings-title') : null;
    const targetTop = target
        ? target.getBoundingClientRect().top - scrollBody.getBoundingClientRect().top + scrollBody.scrollTop
        : scrollBody.scrollHeight;
    scrollBody.scrollTo({ top: targetTop, behavior: 'smooth' });
});

function closeVictoryModal() {
    hidePlainModal('victoryModal', 'victoryModalBackdrop');
}

function startNewGameFromVictory() {
    closeVictoryModal();
    // The game already finished, so there's nothing left to confirm losing.
    doRestart();
}

// Both "restart" and "leave to switch games" reuse the same confirm dialog -
// they're the same action (discard the in-progress game) with different
// destinations, so the dialog's text/action are filled in per caller.
let pendingDiscardAction = null;
let pendingGuardCleanup = false; // see discardGamePopstateGuard above

function openDiscardConfirm({ title, body, confirmLabel, action, cleanupGuardOnConfirm = false }) {
    pendingDiscardAction = action;
    pendingGuardCleanup = cleanupGuardOnConfirm;
    document.getElementById('restartModalTitle').textContent = title;
    document.getElementById('restartModalBody').textContent = body;
    document.getElementById('restartModalConfirmBtn').textContent = confirmLabel;
    showPlainModal('restartModal', 'restartModalBackdrop');
}

function restartGame() {
    closeMenu();
    openDiscardConfirm({
        title: 'Restart Game?',
        body: 'Are you sure you want to restart the game? All current scores and progress will be lost.',
        confirmLabel: 'Restart',
        action: doRestart
    });
}

function closeRestartModal() {
    hidePlainModal('restartModal', 'restartModalBackdrop'); // fire-and-forget: nothing follows this close
    pendingDiscardAction = null;
    // Cancelling leaves any just-re-armed guard entry right where it is
    // (see the popstate listener's cleanupGuardOnConfirm comment) - only a
    // confirm discards it.
    pendingGuardCleanup = false;
}

async function confirmDiscardProgress() {
    const action = pendingDiscardAction;
    const cleanupGuard = pendingGuardCleanup;
    pendingDiscardAction = null;
    pendingGuardCleanup = false;
    // Await the hide (not the closeRestartModal() wrapper, which is
    // fire-and-forget) - the action that follows (doGoHome/doGoToSetup/
    // doRestart) often touches history/hash itself, and firing that at the
    // same time as this modal's own history.back() cleanup races and
    // corrupts both.
    await hidePlainModal('restartModal', 'restartModalBackdrop');
    if (cleanupGuard) await discardGamePopstateGuard();
    if (action) action();
}

function doRestart() {
    currentGameId = null;
    scores = {};
    showSetup(activeGame);
    sessionEnvelopeMeta = null;
    if (!applyingRemoteEnvelope && sync.isInSession() && !sync.isObserver()) {
        sync.pushEnvelope({ phase: 'idle' });
    }
}

// --- Multi-client session sync (see core/sync.js) ---
//
// Applies an incoming room envelope - our own push echoing back (already
// filtered out by sync.js), another client's, or the initial hydration on
// join/reconnect. `applyingRemoteEnvelope` suppresses the local push hooks
// above (startGame/handleGameChange/doGoHome/doRestart) for the duration, so
// applying a remote change never re-broadcasts it right back.
sync.onEnvelope(async (envelope) => {
    applyingRemoteEnvelope = true;
    try {
        // A victory modal open here belongs to the game this session is
        // about to leave (a match-ending 'idle' means someone restarted/left
        // it; a new gameId means someone started a different one) - without
        // closing it first, a client that isn't the one who acted just sits
        // on the landing/new-game screen with the previous game's victory
        // modal still stacked on top. Awaited (not fire-and-forget) since
        // both branches below go on to touch history/hash themselves, which
        // races with the modal's own history cleanup otherwise - see
        // confirmDiscardProgress's identical await for the same reason.
        const leavingGame = envelope.phase === 'idle' || (envelope.phase === 'active' && currentGameId !== envelope.gameId);
        if (leavingGame && isModalOpen('victoryModal')) {
            await hidePlainModal('victoryModal', 'victoryModalBackdrop');
        }

        if (envelope.phase === 'idle') {
            if (isGameScreenActive()) doGoHome();
            return;
        }

        if (envelope.phase === 'config') {
            // A controlling client stepped back to its own setup/config
            // screen without discarding the game (see doGoToSetup()) -
            // every other controlling client ignores this and keeps
            // whatever it's doing, since nothing about the shared game
            // state actually changed. An observer has nothing to watch
            // while nobody's playing, so it goes back to its own landing
            // "waiting" card instead.
            if (sync.isObserver() && isGameScreenActive()) doGoHome();
            return;
        }

        // phase === 'active'
        if (currentGameId !== envelope.gameId) {
            const game = findGame(envelope.gameType);
            if (!game) return;

            activeGame = game;
            players = envelope.players;
            currentGameId = envelope.gameId;
            scores = {};
            standingLabels = {};
            gameTypeLabel = null;
            clearTimeout(finishGameTimer);
            sessionEnvelopeMeta = {
                gameType: envelope.gameType, players: envelope.players,
                matchConfig: envelope.matchConfig, lives: envelope.lives, ring: envelope.ring,
                includeBull: envelope.includeBull,
                multiplier: envelope.multiplier, turnMode: envelope.turnMode
            };

            // This client didn't navigate here itself (startGame() already
            // has the right URL by the time it runs, since it's only ever
            // reached from that game's own setup screen) - it's following
            // the host in via sync, quite possibly from an entirely
            // different URL (landing, another game's setup screen). A plain
            // pushState, not navigateTo() - the game is mounted directly
            // below, so a second handleRoute()-driven showSetup() would
            // just flash and immediately be overwritten.
            history.pushState({ route: true }, '', `/${envelope.gameType}${location.search}`);
            showGame();
            pushHistoryGuard();
            setGameInProgress(true);
            activeGame.mount(document.getElementById('scoreGrid'), {
                players,
                gameId: currentGameId,
                onChange: handleGameChange,
                matchConfig: envelope.matchConfig,
                lives: envelope.lives,
                ring: envelope.ring,
                includeBull: envelope.includeBull,
                multiplier: envelope.multiplier,
                turnMode: envelope.turnMode,
                observer: sync.isObserver()
            });
        }

        if (envelope.controllerState) activeGame.setState(envelope.controllerState);
    } finally {
        applyingRemoteEnvelope = false;
    }
});

function updateSessionUI() {
    const badge = document.getElementById('sessionBadge');
    if (badge) {
        badge.style.display = sync.isInSession() ? 'inline' : 'none';
        badge.textContent = sync.isInSession() ? ` ${sync.currentRoomCode()}${sync.isObserver() ? ' (watching)' : ''}` : '';
    }
    // Drives every observer-only UI restriction (see body.observer-mode in
    // styles.css) - the landing screen's game list vs. "waiting" card, and
    // hiding every navigation/restart control (home, restart, switch game,
    // start new game) regardless of which of app.js's many functions last
    // touched that particular element's own display.
    document.body.classList.toggle('observer-mode', sync.isObserver());
}

// Remembers which room we're in (and whether as an observer) across page
// reloads - a refresh would otherwise drop the SSE connection and land back
// on a plain solo session with no way back in except re-sharing the code.
const SESSION_STORAGE_KEY = 'halveren-session';

function saveStoredSession(code, asObserver) {
    safeSet(SESSION_STORAGE_KEY, JSON.stringify({ code, observer: asObserver }));
}

function loadStoredSession() {
    const raw = safeGet(SESSION_STORAGE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

function clearStoredSession() {
    safeRemove(SESSION_STORAGE_KEY);
}

async function hostSession() {
    // A game already in progress goes into the room as its starting state,
    // so hosting doesn't discard it and anyone who joins lands in it.
    let code;
    try {
        code = await sync.hostRoom(buildSessionEnvelope());
    } catch (e) {
        alert('Hosting a session needs an internet connection.');
        return;
    }
    track('room_created', {});
    saveStoredSession(code, false);
    updateSessionUI();
    showSessionCode(code);
}

async function joinSessionByCode(code, asObserver) {
    let ok;
    try {
        ok = await sync.joinRoom(code, { asObserver });
    } catch (e) {
        // Unreachable server (offline) says nothing about the room itself, so
        // a remembered session is kept for the next load with a connection.
        return false;
    }
    if (!ok) {
        clearStoredSession();
        return false;
    }
    saveStoredSession(sync.currentRoomCode(), asObserver);
    updateSessionUI();
    // Joining (controlling or observing) makes this device part of a live
    // session - keep the screen awake for it the same way an active local
    // game does, whether or not the host has actually started one yet (an
    // observer tablet mounted by the board needs to stay lit while it
    // waits, not just once play begins).
    setGameInProgress(true);
    return true;
}

async function joinSessionByCodeFromModal(code, asObserver) {
    const ok = await joinSessionByCode(code, asObserver);
    if (!ok) {
        alert(navigator.onLine ? 'No session found with that code.' : 'Joining a session needs an internet connection.');
        return;
    }
    track('room_joined', { asObserver });
    closeSessionModal();
}

function updateSessionJoinButtons() {
    const valid = document.getElementById('sessionJoinCode').value.trim().length === 4;
    document.getElementById('sessionJoinBtn').disabled = !valid;
    document.getElementById('sessionJoinObserverBtn').disabled = !valid;
}

function joinSessionFromForm(asObserver) {
    const code = document.getElementById('sessionJoinCode').value.trim();
    if (code.length !== 4) return;
    joinSessionByCodeFromModal(code, asObserver);
}

// --- QR scanning (join code entry) ---
//
// jsQR (vendor/jsqr/jsQR.js, loaded as a classic script - see index.html)
// only decodes a still frame, so this drives its own capture loop: grab the
// live camera frame onto an offscreen canvas every animation frame and hand
// the pixels to jsQR until it finds something. Only offered on
// touch/mobile-shaped devices with a camera - a desktop has no reason to
// scan a code it can just type or paste.
let qrScanStream = null;
let qrScanRAF = null;
const qrScanCanvas = document.createElement('canvas');
const qrScanCtx = qrScanCanvas.getContext('2d', { willReadFrequently: true });

function qrScanSupported() {
    return window.matchMedia?.('(pointer: coarse)').matches
        && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

// jsQR (vendor/jsqr/jsQR.js, ~47KB, no ES module build) is only fetched the
// first time someone actually opens the scanner, not on every page load -
// most sessions never touch it (desktop, or a code just typed/pasted), so
// loading it eagerly was pure wasted bytes on the critical path. Injected as
// a classic <script> (not import()) since it only ships as a UMD bundle
// that assigns window.jsQR rather than exporting anything import() could
// bind to. Cached as a promise so a second open (or a race) doesn't refetch.
let jsQRLoadPromise = null;
function loadJsQR() {
    if (typeof window.jsQR === 'function') return Promise.resolve();
    if (!jsQRLoadPromise) {
        jsQRLoadPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'vendor/jsqr/jsQR.js';
            script.onload = () => resolve();
            script.onerror = () => { jsQRLoadPromise = null; reject(new Error('jsQR failed to load')); };
            document.body.appendChild(script);
        });
    }
    return jsQRLoadPromise;
}

// A session QR encodes the full join link (see joinLinkFor) - pull `join`
// back out of it. Falls back to treating the scanned text as a bare 4-letter
// code, in case it's ever shared that way instead of as a URL.
function extractJoinCodeFromScan(text) {
    try {
        const code = new URL(text).searchParams.get('join');
        if (code) return code;
    } catch (e) {
        // Not a URL - fall through to the bare-code case below.
    }
    const trimmed = text.trim();
    return /^[A-Za-z0-9]{4}$/.test(trimmed) ? trimmed : null;
}

async function openQRScanner() {
    const errorEl = document.getElementById('qrScanError');
    errorEl.style.display = 'none';
    document.getElementById('qrScanModal').style.display = 'block';
    document.getElementById('qrScanModalBackdrop').style.display = 'block';

    try {
        await loadJsQR();
    } catch (e) {
        errorEl.textContent = 'Could not load the QR scanner. Check your connection and try again.';
        errorEl.style.display = 'block';
        return;
    }

    try {
        qrScanStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    } catch (e) {
        errorEl.textContent = 'Camera access is required to scan a QR code.';
        errorEl.style.display = 'block';
        return;
    }

    const video = document.getElementById('qrScanVideo');
    video.srcObject = qrScanStream;
    await video.play();
    scanQRFrame();
}

function scanQRFrame() {
    const video = document.getElementById('qrScanVideo');
    if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth) {
        qrScanCanvas.width = video.videoWidth;
        qrScanCanvas.height = video.videoHeight;
        qrScanCtx.drawImage(video, 0, 0, qrScanCanvas.width, qrScanCanvas.height);
        const imageData = qrScanCtx.getImageData(0, 0, qrScanCanvas.width, qrScanCanvas.height);
        const result = window.jsQR(imageData.data, imageData.width, imageData.height);
        const code = result && result.data ? extractJoinCodeFromScan(result.data) : null;
        if (code) {
            document.getElementById('sessionJoinCode').value = code.toUpperCase();
            updateSessionJoinButtons();
            closeQRScanner();
            return;
        }
    }
    qrScanRAF = requestAnimationFrame(scanQRFrame);
}

// Not wired through core/modal.js's back-button guard (unlike every other
// modal here) - closing needs to reliably stop the camera stream on every
// path (Cancel, backdrop click, or navigating away entirely), and this is
// the one function all of those already funnel through.
function closeQRScanner() {
    if (qrScanRAF) cancelAnimationFrame(qrScanRAF);
    qrScanRAF = null;
    if (qrScanStream) {
        qrScanStream.getTracks().forEach(track => track.stop());
        qrScanStream = null;
    }
    document.getElementById('qrScanModal').style.display = 'none';
    document.getElementById('qrScanModalBackdrop').style.display = 'none';
}

function leaveSession() {
    sync.leaveRoom();
    clearStoredSession();
    updateSessionUI();
    // The session itself no longer needs the screen kept awake - but if a
    // local game is still actively on screen independent of the session
    // (leaving the session doesn't leave the game itself), that already has
    // its own claim on the wake lock via startGame()/the sync join above,
    // so only actually release it when there's nothing left wanting it.
    if (!isGameScreenActive()) setGameInProgress(false);
    closeSessionModal();
}

function openSessionModal() {
    if (sync.isInSession()) {
        showSessionCode(sync.currentRoomCode());
    } else {
        document.getElementById('sessionCodeView').style.display = 'none';
        document.getElementById('sessionJoinView').style.display = 'block';
    }
    showPlainModal('sessionModal', 'sessionModalBackdrop');
}

function closeSessionModal() {
    hidePlainModal('sessionModal', 'sessionModalBackdrop');
}

function joinLinkFor(code) {
    return `${location.origin}${location.pathname}?join=${code}`;
}

function showSessionCode(code) {
    document.getElementById('sessionJoinView').style.display = 'none';
    document.getElementById('sessionCodeView').style.display = 'block';
    document.getElementById('sessionCodeDisplay').textContent = code;
    updateObserverToggleIcon();

    const qrEl = document.getElementById('sessionQR');
    if (qrEl) {
        const qr = qrcode(0, 'M'); // typeNumber 0 = smallest size that fits the data
        qr.addData(joinLinkFor(code));
        qr.make();
        qrEl.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
    }
}

function copySessionLink() {
    navigator.clipboard?.writeText(joinLinkFor(sync.currentRoomCode()));
}

function updateObserverToggleIcon() {
    const icon = document.getElementById('sessionObserverToggleIcon');
    if (!icon) return;
    const on = sync.isObserver();
    icon.className = on ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off';
    document.getElementById('sessionObserverToggle').setAttribute('aria-pressed', String(on));
}

// Lets a client already connected to a session flip between controlling and
// observing on the fly, without leaving/rejoining the room (see
// sync.setObserver's own comment - the connection itself is untouched).
function toggleObserverMode() {
    const next = !sync.isObserver();
    sync.setObserver(next);
    saveStoredSession(sync.currentRoomCode(), next);
    updateSessionUI();
    updateObserverToggleIcon();
    remountCurrentGameForObserverChange();
}

// A mounted controller bakes "observer" into its initial render (no input
// wired at all, not just visually blocked - see e.g. x01-game-controller.js's
// mount()), so switching modes mid-game means actually remounting it, not
// just re-rendering - getState() first to carry the live match forward
// through that remount instead of restarting it.
function remountCurrentGameForObserverChange() {
    if (!isGameScreenActive() || !activeGame || !sessionEnvelopeMeta) return;
    const snapshot = activeGame.getState();
    // The roster the game was actually started with, not the shared setup
    // list - that one can hold Computer players configured for a different,
    // CPU-capable game (see playersForActiveGame()).
    const gamePlayers = sessionEnvelopeMeta.players;
    activeGame.mount(document.getElementById('scoreGrid'), {
        players: gamePlayers,
        gameId: currentGameId,
        onChange: handleGameChange,
        matchConfig: sessionEnvelopeMeta.matchConfig,
        lives: sessionEnvelopeMeta.lives,
        ring: sessionEnvelopeMeta.ring,
        includeBull: sessionEnvelopeMeta.includeBull,
        multiplier: sessionEnvelopeMeta.multiplier,
        turnMode: sessionEnvelopeMeta.turnMode,
        observer: sync.isObserver(),
        cpuPlayers: buildCpuPlayers(gamePlayers)
    });
    activeGame.setState(snapshot);
}

// ?join=CODE (optionally &observe=1) auto-joins a session on load, before
// the normal landing/hash routing below runs - a fresh share link always
// wins over whatever was previously stored. Absent that, a room this device
// already hosted/joined before a reload is rejoined automatically (a plain
// refresh would otherwise silently drop the SSE connection with no way back
// in except re-sharing the code); a stored code that's no longer valid (the
// room expired, or the server restarted) just falls through to the normal
// solo landing screen.
async function restoreSessionOnLoad() {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('join');
    if (urlCode) {
        const asObserver = params.get('observe') === '1';
        const ok = await joinSessionByCode(urlCode, asObserver);
        if (ok) {
            track('room_joined', { asObserver });
            // The code's done its job (and is already remembered in
            // localStorage for a later reload, see joinSessionByCode) - a
            // reshared/bookmarked/refreshed copy of this same link
            // shouldn't keep carrying it around, or a screenshot of the URL
            // bar would leak a working join code. replaceState, not
            // navigateTo()/pushState - this is a cleanup of the entry
            // that's already here, not a new navigation, and any pathname
            // the join itself just set (see the sync.onEnvelope handler)
            // must survive untouched.
            const url = new URL(window.location.href);
            url.searchParams.delete('join');
            url.searchParams.delete('observe');
            history.replaceState(history.state, '', url);
        }
        return;
    }

    const stored = loadStoredSession();
    if (stored) {
        await joinSessionByCode(stored.code, stored.observer);
    }
}

// --- Statistics ---

function gameNameFor(gameType) {
    // Games saved before gameType existed have none stored at all - Halve It
    // was the only game back then, so that's the correct fallback rather
    // than showing "undefined".
    const game = findGame(gameType) || findGame('halveit');
    return game ? game.definition.name : gameType;
}

async function showStatistics() {
    const games = await getAllGames();
    if (games) {
        displayGameHistory(games);
    } else {
        document.getElementById('gameHistory').innerHTML = '<div class="no-games">Game history is not available in this environment</div>';
    }
    showPlainModal('statsModal', 'statsModalBackdrop');
}

function displayGameHistory(games) {
    let historyHTML = '';
    if (games.length === 0) {
        historyHTML = '<div class="no-games">No games played yet</div>';
    } else {
        historyHTML = games.map(game => {
            const date = new Date(game.date);
            const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

            return `
                <div class="game-item">
                    <div class="game-date"><span class="game-type-tag">${game.gameTypeLabel || gameNameFor(game.gameType)}</span> ${dateStr}</div>
                    <div class="game-scores">
                        ${game.players.map((player, index) => `
                            <div class="score-row">
                                <span class="score-row-name">${index === 0 ? '🏆 ' : ''}${player.name}</span>
                                <span class="score-row-values">
                                    ${player.label ? `<span class="score-row-label">${player.label}</span>` : ''}
                                    <span class="score-row-value" style="color: ${rankColor(index)}">${player.score}</span>
                                </span>
                            </div>
                        `).join('')}
                    </div>
                </div>
            `;
        }).join('');
    }

    document.getElementById('gameHistory').innerHTML = historyHTML;
}

function closeStatsModal() {
    hidePlainModal('statsModal', 'statsModalBackdrop');
}

// --- Version / changelog (bottom of the side menu) ---
//
// GET /api/version and /api/changelogs (server/index.js) hide the actual
// dev-vs-production difference server-side (a live scan of changelogs/ vs.
// the single bundle build-changelog.js produces at build time) - this side
// just calls them the same way regardless of which one it's actually
// talking to.

async function loadVersionInfo() {
    const label = document.getElementById('menuVersionLabel');
    const info = await fetchVersionInfo();
    if (!info) {
        label.textContent = ''; // no version endpoint reachable - just omit it rather than show something wrong
        return;
    }
    label.textContent = `v${info.version}${info.dev ? ' (local)' : ''}`;
    announceUpdatedVersion(info);
}

// --- App updates (see core/pwa.js) ---
//
// A new version installs in the background; the navbar chip appears once it's
// ready and applying it reloads the app. Game state survives a reload only
// inside a room, so applying mid-game outside one asks first.

const LAST_VERSION_STORAGE_KEY = 'halveren-last-version';
const UPDATED_TOAST_MS = 8000;
let updatedToastTimer = null;

function showUpdateChip() {
    document.getElementById('updateChip').style.display = 'flex';
}

function onUpdateChipClick() {
    const inUnsyncedGame = isGameScreenActive() && activeGame && activeGame.hasProgress && activeGame.hasProgress() && !sync.isInSession();
    if (inUnsyncedGame) {
        openDiscardConfirm({
            title: 'Update Now?',
            body: 'Updating reloads the app, and your current game will be lost.',
            confirmLabel: 'Update',
            action: startUpdate
        });
        return;
    }
    startUpdate();
}

function startUpdate() {
    const chip = document.getElementById('updateChip');
    chip.disabled = true;
    document.getElementById('updateChipLabel').textContent = 'Updating…';
    applyUpdate();
}

// Shown once, right after a reload onto a newer version than this device last ran.
function announceUpdatedVersion({ version, dev }) {
    const previous = safeGet(LAST_VERSION_STORAGE_KEY);
    safeSet(LAST_VERSION_STORAGE_KEY, version);
    if (dev || !previous || previous === version) return;
    const el = document.getElementById('updatedToast');
    el.innerHTML = `<span class="updated-toast-message"></span><button class="feedback-toast-view updated-toast-link">What's new?</button>`;
    el.querySelector('.updated-toast-message').textContent = `Updated to v${version}`;
    el.querySelector('.updated-toast-link').addEventListener('click', () => {
        hideUpdatedToast();
        showChangelogModal();
    });
    el.classList.add('show');
    clearTimeout(updatedToastTimer);
    updatedToastTimer = setTimeout(hideUpdatedToast, UPDATED_TOAST_MS);
}

function hideUpdatedToast() {
    document.getElementById('updatedToast').classList.remove('show');
}

async function checkForUpdatesManually() {
    const button = document.getElementById('checkUpdateBtn');
    const label = document.getElementById('checkUpdateLabel');
    button.disabled = true;
    label.textContent = 'Checking…';
    const result = await checkForUpdates();
    button.disabled = false;
    if (result === 'ready') {
        closeMenu();
        showUpdateChip();
        label.textContent = 'Check for updates';
        return;
    }
    label.textContent = result === 'error' ? "Couldn't check" : 'Up to date';
    setTimeout(() => { label.textContent = 'Check for updates'; }, 3000);
}

watchForUpdates({
    onReady: showUpdateChip,
    onRequired: () => { document.getElementById('updateRequired').style.display = 'flex'; }
});

// Fetched once and reused for the rest of the session - these don't change
// without a page reload (a fresh deploy or a server restart in dev), so
// there's no reason to re-fetch on every open.
let cachedChangelogEntries = null;

async function showChangelogModal() {
    const list = document.getElementById('changelogList');
    if (!cachedChangelogEntries) {
        list.innerHTML = '<div class="no-games">Loading&hellip;</div>';
        showPlainModal('changelogModal', 'changelogModalBackdrop');
        try {
            const res = await fetch('/api/changelogs');
            cachedChangelogEntries = (await res.json()).entries;
        } catch (e) {
            list.innerHTML = '<div class="no-games">Couldn\'t load the changelog</div>';
            return;
        }
    } else {
        showPlainModal('changelogModal', 'changelogModalBackdrop');
    }

    list.innerHTML = cachedChangelogEntries.length === 0
        ? '<div class="no-games">Nothing here yet</div>'
        : cachedChangelogEntries.map(entry => `<div class="changelog-entry">${entry.html}</div>`).join('');
}

function closeChangelogModal() {
    hidePlainModal('changelogModal', 'changelogModalBackdrop');
}

// --- Feedback & Bug Reports (side menu) ---
//
// The modal shell only - opening/closing it and mounting its content. The
// list/detail/new-edit rendering itself lives in components/feedback-board.js,
// shared with the /admin/feedback page (see that file's own comment) so
// there's exactly one implementation of it, not two.

async function showFeedbackModal({ scrollToUpdated = false } = {}) {
    showPlainModal('feedbackModal', 'feedbackModalBackdrop');
    mountFeedbackBoard({
        containerEl: document.getElementById('feedbackModalBody'),
        api: feedbackApi,
        mode: 'user',
        scrollToUpdated,
        updates: { getForItem: feedbackUpdates.getUnreadForItem, markSeen: feedbackUpdates.markItemSeen }
    });
}

let feedbackToastTimer = null;

function showFeedbackToast(count) {
    const el = document.getElementById('feedbackToast');
    const message = count === 1 ? '1 of your feedback items has an update' : `${count} of your feedback items have updates`;
    // "View" is just a cue (styled like the x01 toast's Undo) - the whole toast is the click target.
    el.innerHTML = `<span class="feedback-toast-message"></span><span class="feedback-toast-view">View</span>`;
    el.querySelector('.feedback-toast-message').textContent = message;
    el.classList.add('show');
    clearTimeout(feedbackToastTimer);
    feedbackToastTimer = setTimeout(hideFeedbackToast, 6000);
}

function hideFeedbackToast() {
    document.getElementById('feedbackToast').classList.remove('show');
}

function openFeedbackFromToast() {
    hideFeedbackToast();
    showFeedbackModal({ scrollToUpdated: true });
}

// Badges the menu (and the hamburger, so it shows before the menu is even
// open) whenever some of this device's feedback has changes it hasn't seen.
feedbackUpdates.onUnreadChange((count) => {
    const badge = document.getElementById('feedbackMenuBadge');
    badge.textContent = String(count);
    badge.style.display = count > 0 ? 'inline-block' : 'none';
    document.querySelector('.hamburger').classList.toggle('has-updates', count > 0);
});

// Once per page load, and never again for changes that were already
// announced (see takeToastCount) - the badge stays either way.
async function checkFeedbackUpdatesOnLoad() {
    await feedbackUpdates.refreshUnread();
    const count = feedbackUpdates.takeToastCount();
    if (count > 0) showFeedbackToast(count);
}

function closeFeedbackModal() {
    hidePlainModal('feedbackModal', 'feedbackModalBackdrop');
}

// --- Game info (the info icon next to the game name on the setup screen) ---

function showGameInfoModal() {
    if (!activeGame) return;
    document.getElementById('gameInfoModalTitle').textContent = activeGame.definition.name;
    document.getElementById('gameInfoModalBody').textContent =
        activeGame.definition.description || 'No description yet.';
    showPlainModal('gameInfoModal', 'gameInfoModalBackdrop');
}

function closeGameInfoModal() {
    hidePlainModal('gameInfoModal', 'gameInfoModalBackdrop');
}

// --- Fullscreen ---

function toggleFullscreen() {
    if (!document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.msFullscreenElement) {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { // Safari
            elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { // IE11
            elem.msRequestFullscreen();
        }
        fullscreen = true;
    } else {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { // Safari
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE11
            document.msExitFullscreen();
        }
        fullscreen = false;
    }
}

// Allow Enter key to add player
document.getElementById('playerName').addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
        addPlayer();
    }
});

document.getElementById('playerName').addEventListener('input', updateAddPlayerButtonState);

document.getElementById('sessionJoinCode').addEventListener('input', updateSessionJoinButtons);

document.addEventListener('keydown', function(e) {
    if (document.getElementById('sideMenu').classList.contains('active') && e.key === 'Escape') {
        closeMenu();
    }
    if (isModalOpen('restartModal') && e.key === 'Escape') {
        closeRestartModal();
    }
    if (isModalOpen('statsModal') && e.key === 'Escape') {
        closeStatsModal();
    }
    if (isModalOpen('changelogModal') && e.key === 'Escape') {
        closeChangelogModal();
    }
    if (isModalOpen('feedbackModal') && e.key === 'Escape') {
        closeFeedbackModal();
    }
    if (isModalOpen('victoryModal') && e.key === 'Escape') {
        closeVictoryModal();
    }
    if (isModalOpen('qrScanModal') && e.key === 'Escape') {
        closeQRScanner();
    }
});

window.addEventListener('DOMContentLoaded', async function() {
    renderThemeOptions();
    maybeShowMenuHint();
    initPlayersDragReorder();
    renderDifficultyMenu();
    document.getElementById('notrackIndicator').style.display = isTrackingDisabled() ? 'flex' : 'none';

    // initDB() never rejects (persistence is a nice-to-have, not a
    // requirement - see its own comment), so routing below always runs
    // regardless of whether IndexedDB is actually available.
    await initDB();

    const loaded = await loadPlayersFromStorage();
    if (loaded) {
        players = loaded.players;
        playerConfigs = loaded.playerConfigs;
    }
    // Unconditional (not just inside the `if` above) - with no saved roster
    // at all, `players` stays the empty array it already started as, and
    // #playersList still needs its empty-state hint instead of being left
    // as whatever static markup index.html happened to ship with.
    updatePlayersList();
    updateAddPlayerButtonState(); // covers a browser restoring a typed value into #playerName on reload, which index.html's own `disabled` attribute wouldn't know about

    document.getElementById('scanQRBtn').style.display = qrScanSupported() ? 'flex' : 'none';
    // Deferred to idle time (not fired eagerly here) - the label it fills is
    // hidden inside the collapsed side menu, not part of the initial view,
    // so there's no reason for it to sit on the critical request chain
    // behind app.js (flagged by PageSpeed's "Avoid chaining critical
    // requests"). requestIdleCallback isn't in Safari, hence the fallback.
    if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadVersionInfo());
    } else {
        setTimeout(loadVersionInfo, 200);
    }

    checkFeedbackUpdatesOnLoad();

    await restoreSessionOnLoad();
    updateSessionUI();
    // Joining mid-game (see the sync.onEnvelope handler above) already
    // mounted the game screen directly - the normal hash-based routing below
    // would otherwise immediately override that back to the landing screen
    // (an empty/unrelated hash is the common case when arriving via a share
    // link) since it doesn't know a session just placed us here.
    if (!isGameScreenActive()) handleRoute();
});

// The notrackIndicator button only ever shows while tracking is already
// off for this device (see the DOMContentLoaded wiring above), so this only
// ever needs to turn it back on - reloading (not just clearing the flag in
// place) keeps this the one code path metrics.js's own ?notrack handling
// has to support, rather than a second, separate "clear it live" one.
function reEnableTracking() {
    const url = new URL(location.href);
    url.searchParams.set('notrack', '0');
    location.href = url.toString();
}

// Referenced from onclick="..." attributes in index.html - those always
// resolve against global scope.
Object.assign(window, {
    addPlayer, startGame, goHome,
    setAddPlayerType, toggleDifficultyMenu,
    restartGame, closeRestartModal, confirmDiscardProgress,
    closeVictoryModal, startNewGameFromVictory,
    showStatistics, closeStatsModal,
    showChangelogModal, closeChangelogModal,
    onUpdateChipClick, checkForUpdatesManually, applyUpdate,
    showFeedbackModal, closeFeedbackModal, openFeedbackFromToast,
    showGameInfoModal, closeGameInfoModal,
    toggleFullscreen,
    openSessionModal, closeSessionModal, hostSession, joinSessionFromForm,
    leaveSession, copySessionLink, openQRScanner, closeQRScanner,
    toggleObserverMode, reEnableTracking
});
